<template>
  <div>
    <!-- Linha 1: Resumo Financeiro -->
    <b-row class="mb-3">
      <!-- Saldo Disponível -->
      <b-col md="4" sm="6">
        <b-card class="shadow-sm border-0 h-100">
          <div class="d-flex align-items-start">
            <feather-icon icon="DollarSignIcon" size="18" class="text-success mr-2 mt-1"/>
            <div>
              <p class="card-text small text-muted mb-0">Saldo Disponível</p>
              <h4 class="font-weight-bolder mb-1">R$ {{ saldoData.saldo_disponivel }}</h4>
            </div>
          </div>
        </b-card>
      </b-col>

      <!-- Em Processamento -->
      <b-col md="4" sm="6">
        <b-card class="shadow-sm border-0 h-100">
          <div class="d-flex align-items-start">
            <feather-icon icon="ClockIcon" size="18" class="text-warning mr-2 mt-1"/>
            <div>
              <p class="card-text small text-muted mb-0">Em Processamento</p>
              <h4 class="font-weight-bolder mb-1">R$ {{ saldoData.saldo_processamento }}</h4>
            </div>
          </div>
        </b-card>
      </b-col>

      <!-- Total Resgatado -->
      <b-col md="4" sm="12">
        <b-card class="shadow-sm border-0 h-100">
          <div class="d-flex align-items-start">
            <feather-icon icon="CheckCircleIcon" size="18" class="text-info mr-2 mt-1"/>
            <div>
              <p class="card-text small text-muted mb-0">Total Resgatado</p>
              <h4 class="font-weight-bolder mb-1">R$ {{ saldoData.saldo_resgatado }}</h4>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Linha 2: Card de Resgate -->
    <b-card no-body class="shadow-sm border-0">
      <b-card-header>
        <div class="d-flex justify-content-between align-items-center w-100">
          <b-card-title class="d-flex align-items-center mb-0">
            <feather-icon icon="DownloadIcon" size="18" class="mr-1"/>
            Resgate de Valores
          </b-card-title>
          <b-button
            variant="outline-primary"
            size="sm"
            @click="verMinhasSolicitacoes"
          >
            <feather-icon icon="ListIcon" size="16" class="mr-1"/>
            Minhas solicitações
          </b-button>
        </div>
      </b-card-header>

      <b-card-body>
        <h5 class="mb-3">Solicitar novo resgate</h5>

        <!-- Alerta de Informações -->
        <b-alert show variant="light-info" class="mb-4">
          <div class="alert-body d-flex align-items-start">
            <feather-icon icon="InfoIcon" size="18" class="mr-2 mt-1 flex-shrink-0"/>
            <div>
              <h6 class="alert-heading">Informações sobre resgates</h6>
              <span class="small">Os resgates são processados em até 3 dias úteis após a aprovação. O valor mínimo para resgate é de R$ 100,00. Uma vez solicitado, o valor ficará reservado até a conclusão da transferência.</span>
            </div>
          </div>
        </b-alert>

        <!-- Formulário de Resgate -->
        <b-form @submit.prevent="solicitarResgate">
          <h6 class="mb-3">Informe o valor do resgate</h6>
          <b-row class="mb-3">
            <b-col md="6">
              <b-form-group label="Valor a resgatar" label-for="valor-resgate">
                <b-form-input
                  id="valor-resgate"
                  v-model="form.valor"
                  type="text"
                  placeholder="R$ 0,00"
                  required
                  @input="form.valor = form.valor.replace(/[^0-9.,]/g, '').replace(',', '.')"
                />
                <b-form-text class="small text-muted">
                  Valor mínimo: R$ 100,00 | Valor máximo: R$ {{ saldoData.saldo_disponivel }}
                </b-form-text>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Descrição (opcional)" label-for="descricao-resgate">
                <b-form-input
                  id="descricao-resgate"
                  v-model="form.descricao"
                  placeholder="Ex: Resgate mensal de maio"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <h6 class="mb-3">Selecione o método de recebimento</h6>
          <b-row class="mb-3">
            <!-- Opção PIX -->
            <b-col md="6">
              <div
                :class="['p-3 rounded border cursor-pointer h-100 d-flex align-items-start', { 'border-primary bg-light-primary': form.metodo === 'pix' }]"
                @click="form.metodo = 'pix'"
              >
                <feather-icon icon="GridIcon" size="20" class="mr-2 mt-1 text-primary"/>
                <div>
                  <h6 class="font-weight-bolder mb-0">PIX</h6>
                  <p class="card-text small text-muted mb-0">Transferência instantânea para sua chave PIX</p>
                </div>
              </div>
            </b-col>

            <!-- Opção Transferência Bancária -->
            <b-col md="6">
              <div
                :class="['p-3 rounded border cursor-pointer h-100 d-flex align-items-start', { 'border-primary bg-light-primary': form.metodo === 'transferencia' }]"
                @click="form.metodo = 'transferencia'"
              >
                <feather-icon icon="HomeIcon" size="20" class="mr-2 mt-1 text-primary"/>
                <div>
                  <h6 class="font-weight-bolder mb-0">Transferência Bancária</h6>
                  <p class="card-text small text-muted mb-0">Depósito em conta corrente ou poupança</p>
                </div>
              </div>
            </b-col>
          </b-row>

          <!-- Campos Condicionais PIX -->
          <div v-if="form.metodo === 'pix'">
            <hr>
            <h6 class="mb-3">Dados PIX</h6>
            <b-row>
              <b-col md="6">
                <b-form-group label="Tipo de Chave PIX" label-for="tipo-chave-pix">
                  <b-form-select
                    id="tipo-chave-pix"
                    v-model="form.pix.tipoChave"
                    :options="tiposChavePix"
                    required
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Chave PIX" label-for="chave-pix">
                  <b-form-input
                    id="chave-pix"
                    v-model="form.pix.chave"
                    placeholder="Informe sua chave PIX"
                    required
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <!-- Campos Condicionais Transferência (Placeholder) -->
          <div v-if="form.metodo === 'transferencia'">
            <hr>
            <h6 class="mb-3">Dados Bancários</h6>
            <b-alert show variant="light-secondary">
              <feather-icon icon="InfoIcon" size="16" class="mr-1"/>
              Utilizaremos os dados bancários informados no seu cadastro para realizar a transferência.
            </b-alert>
          </div>

          <!-- Upload Nota Fiscal -->
          <div v-if="form.metodo">
            <hr>
            <h6 class="mb-3">Nota Fiscal</h6>
            <b-alert show variant="light-warning" class="mb-3">
              <div class="alert-body d-flex align-items-start">
                <feather-icon icon="FileTextIcon" size="18" class="mr-2 mt-1 flex-shrink-0 text-warning"/>
                <div>
                  <h6 class="alert-heading">Documento obrigatório</h6>
                  <span class="small">Para processar o resgate é necessário anexar a nota fiscal correspondente ao valor solicitado. Formatos aceitos: PDF, JPEG, PNG (máx. 10 MB).</span>
                </div>
              </div>
            </b-alert>
            <b-form-group label="Anexar Nota Fiscal *" label-for="nota-fiscal">
              <b-form-file
                id="nota-fiscal"
                v-model="form.notaFiscal"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                placeholder="Clique para selecionar ou arraste o arquivo..."
                drop-placeholder="Solte o arquivo aqui..."
                browse-text="Selecionar"
                required
              />
            </b-form-group>
          </div>

          <!-- Botão de Solicitação -->
          <hr v-if="form.metodo">
          <div class="text-right mt-3">
            <b-button
              type="submit"
              variant="primary"
              :disabled="!form.metodo || !form.valor || Number(form.valor) < 100 || !form.notaFiscal">
              Solicitar Resgate
            </b-button>
          </div>

        </b-form>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BCardBody, BCardHeader, BCardTitle, BAlert, BForm, BFormGroup, BFormInput, BFormText, BFormSelect, BButton, BFormFile,
} from 'bootstrap-vue'
// eslint-disable-next-line import/no-cycle
import { getUserData } from '@/auth/utils'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from '@axios'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BAlert,
    BForm,
    BFormGroup,
    BFormInput,
    BFormText,
    BFormSelect,
    BButton,
    BFormFile,
    FeatherIcon,
  },
  data() {
    const userData = getUserData()
    return {
      form: {
        valor: null,
        descricao: '',
        metodo: null,
        pix: {
          tipoChave: null,
          chave: '',
        },
        notaFiscal: null,
      },
      tiposChavePix: [
        { value: null, text: 'Selecione o tipo de chave' },
        { value: 'CPF', text: 'CPF' },
        { value: 'CNPJ', text: 'CNPJ' },
        { value: 'Email', text: 'E-mail' },
        { value: 'Telefone', text: 'Telefone' },
        { value: 'Chave aleatoria', text: 'Chave Aleatória' },
      ],
      userData,
      saldoData: {
        saldo_disponivel: 0,
        saldo_processamento: 0,
        saldo_resgatado: 0,
      },
      loading: false,
    }
  },
  mounted() {
    // Chama o método para buscar os dados de saldo quando o componente é montado
    this.fetchSaldoUsuario()
  },
  methods: {
    async fetchSaldoUsuario() {
      try {
        this.loading = true
        const idUsuario = this.userData?.id // Obtém o ID do usuário logado
        if (!idUsuario) {
          console.error('ID do usuário não encontrado')
          return
        }
        // Faz a requisição para a API
        const { data } = await axios.get(`/saldo/usuario/${idUsuario}`)
        // Atualiza os dados do usuário com os dados recebidos da API
        this.saldoData = data
      } catch (error) {
        console.error('Erro ao buscar saldo:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Usuário sem saldo no momento',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Caso identifique isso como um problema, acione o suporte.',
          },
        })
      } finally {
        this.loading = false
      }
    },
    async solicitarResgate() {
      // Verificação de valor mínimo
      if (!this.form.valor || Number(this.form.valor) < 100) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Valor Inválido',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
            text: 'O valor mínimo para resgate é de R$ 100,00.',
          },
        })
        return
      }

      // Verificação de saldo disponível
      if (Number(this.form.valor) > Number(this.saldoData.saldo_disponivel)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Saldo Insuficiente',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
            text: 'O valor solicitado excede o seu saldo disponível.',
          },
        })
        return
      }

      if (!this.form.notaFiscal) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Nota Fiscal Obrigatória',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
            text: 'Por favor, anexe a nota fiscal para prosseguir com o resgate.',
          },
        })
        return
      }

      try {
        this.loading = true
        const formData = new FormData()
        formData.append('id_usuario', this.userData.id)
        formData.append('valor', Number(this.form.valor))
        formData.append('descricao', this.form.descricao || '')
        formData.append('metodo_recebimento', this.form.metodo)
        if (this.form.pix.tipoChave) formData.append('tipo_pix', this.form.pix.tipoChave)
        if (this.form.pix.chave) formData.append('chave_pix', this.form.pix.chave)
        formData.append('nota_fiscal', this.form.notaFiscal)

        const { data } = await axios.post('/saque', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        try {
          // 1. Calcular novos saldos
          const valorSaque = Number(this.form.valor)
          const novoSaldoDisponivel = Number(this.saldoData.saldo_disponivel) - valorSaque
          const novoSaldoProcessamento = Number(this.saldoData.saldo_processamento) + valorSaque
          // 2. Atualizar saldo no backend
          await axios.put(`/saldo/${this.userData.id}`, {
            saldo_disponivel: novoSaldoDisponivel,
            saldo_processamento: novoSaldoProcessamento,
          })
          console.log('Saldo atualizado com sucesso:', {
            disponivel: novoSaldoDisponivel,
            processamento: novoSaldoProcessamento,
          })
        } catch (saldoError) {
          console.error('Erro ao atualizar saldo:', saldoError)
          // Não bloquear o fluxo principal se a atualização de saldo falhar
        }
        // Limpar formulário
        this.resetFormulario()
        // Atualizar saldo após o resgate
        this.fetchSaldoUsuario()
        // Exibir mensagem de sucesso
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Solicitação Enviada',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'Sua solicitação de resgate foi enviada para análise.',
          },
        })
      } catch (error) {
        console.error('Erro ao solicitar resgate:', error)
        // Tratamento de erros específicos do backend
        const mensagemErro = error.response?.data?.message || 'Não foi possível processar sua solicitação de resgate.'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro no Resgate',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: mensagemErro,
          },
        })
      } finally {
        this.loading = false
      }
    },
    verMinhasSolicitacoes() {
      this.$router.push({ name: 'app-financeiro-solicitacoes' })
    },
    resetFormulario() {
      this.form = {
        valor: null,
        descricao: '',
        metodo: null,
        pix: {
          tipoChave: null,
          chave: '',
        },
        notaFiscal: null,
      }
    },
  },
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
/* Ajustes para o alerta light-info */
.alert.alert-light-info {
  background-color: rgba(0, 207, 232, 0.12);
  border-color: rgba(0, 207, 232, 0.2);
  color: #00cfe8;
}
.alert.alert-light-info .alert-heading {
  color: inherit;
}
.alert.alert-light-info .feather-icon {
  color: #00cfe8;
}

/* Estilo para card selecionado */
.border-primary {
  border-color: #7367f0 !important;
  box-shadow: 0 0 0 1px #7367f0;
}
.bg-light-primary {
  background-color: rgba(115, 103, 240, 0.05) !important;
}
</style>
